// Human-readable labels for the lab-result enum codes. The backend stores
// machine keys (e.g. `legionella_pneumophila_sg1`); users should see proper
// names (e.g. "L. pneumophila SG1"). Units/qualifiers already have labels in
// LabResultBandPill — this module owns analyte names.

import type { LabSample } from '../types';

const ANALYTE_LABELS: Record<string, string> = {
  legionella_total: 'Legionella (total)',
  legionella_pneumophila: 'Legionella pneumophila',
  legionella_pneumophila_sg1: 'L. pneumophila SG1',
  legionella_pneumophila_sg2_14: 'L. pneumophila SG2–14',
  legionella_non_pneumophila: 'Legionella (non-pneumophila)',
  hpc: 'HPC (heterotrophic plate count)',
  total_coliforms: 'Total coliforms',
  e_coli: 'E. coli',
  pseudomonas_aeruginosa: 'Pseudomonas aeruginosa',
};

/**
 * Human-readable name for an analyte code.
 *
 * Falls back to a title-cased version of the raw key so an unmapped or
 * newly-added analyte still renders legibly rather than as a raw enum.
 *
 * @param key - The analyte enum code (e.g. `legionella_pneumophila_sg1`).
 * @returns A display label (e.g. `L. pneumophila SG1`).
 */
export function analyteLabel(key: string | null | undefined): string {
  if (!key) return '—';
  if (ANALYTE_LABELS[key]) return ANALYTE_LABELS[key];
  return key
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/**
 * Builds a concise display label for a lab sample.
 *
 * @param sample - The sample identity fields used by the fallback chain.
 * @returns The first non-empty sample label, or the shortened sample ID.
 */
export function sampleLabel(
  sample: Pick<
    LabSample,
    'sample_id_lab' | 'sample_id_client' | 'location_description' | 'id'
  >,
): string {
  const candidates = [
    sample.location_description,
    sample.sample_id_lab,
    sample.sample_id_client,
  ];
  const label = candidates.find((candidate) => candidate?.trim());

  return label?.trim() ?? sample.id.slice(0, 8);
}

/** Display an incident workflow step in plain English. */
export function incidentStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    detected: 'Detected', acknowledged: 'Acknowledged', actions_issued: 'Actions issued',
    in_rectification: 'In rectification', retest_ordered: 'Re-test ordered',
    retest_received: 'Re-test received', closed: 'Closed',
  };
  return labels[status] || status.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
}
